import { safeSessionStorage } from './browserStorage';
import {
  buildAuthUrl,
  getDefaultAppOrigin,
  isAuthHostname,
  isLocalAuthHostname,
  isTrustedWerseeReturnOrigin,
  resolveAuthReturnUrl,
  resolveInternalAuthReturnUrl,
} from './authRouting';
import { supabase, supabaseUrl } from './supabase';

export const WERSEE_WEB_OAUTH_CLIENT_ID = (
  import.meta.env.VITE_WERSEE_WEB_OAUTH_CLIENT_ID
  || '076d20ee-48c7-4d83-abc5-eeb3167acf48'
).trim();

export const WERSEE_WEB_OAUTH_REDIRECT_URI = 'https://www.wersee.com/auth/handoff';
export const authHandoffStorageKey = 'wersee:first-party-auth-handoff';
const authHandoffMaxAgeMs = 10 * 60 * 1000;

type AuthHandoffRecord = {
  codeVerifier: string;
  createdAt: number;
  redirectUri: string;
  returnUrl: string;
  state: string;
};

type OAuthTokenResponse = {
  access_token?: string;
  refresh_token?: string;
  error?: string;
  error_description?: string;
};

const base64Url = (bytes: Uint8Array) => {
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const randomValue = (length = 32) => {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
};

const sha256Challenge = async (verifier: string) => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
};

const isPkceValue = (value: string | null, minimum = 32) => (
  Boolean(value) && value!.length >= minimum && value!.length <= 128 && /^[A-Za-z0-9_-]+$/.test(value!)
);

const readHandoffRecord = () => {
  const raw = safeSessionStorage.getItem(authHandoffStorageKey);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<AuthHandoffRecord>;
    if (
      typeof value.createdAt !== 'number'
      || Date.now() - value.createdAt > authHandoffMaxAgeMs
      || !isPkceValue(value.codeVerifier || null, 43)
      || !isPkceValue(value.state || null)
      || typeof value.redirectUri !== 'string'
      || typeof value.returnUrl !== 'string'
    ) {
      safeSessionStorage.removeItem(authHandoffStorageKey);
      return null;
    }
    return value as AuthHandoffRecord;
  } catch {
    safeSessionStorage.removeItem(authHandoffStorageKey);
    return null;
  }
};

export const createCentralAuthStartUrl = async (
  authPath: string,
  rawReturnUrl?: string | null,
) => {
  const returnUrl = resolveAuthReturnUrl(rawReturnUrl);
  const codeVerifier = randomValue(64);
  const state = randomValue(32);
  const codeChallenge = await sha256Challenge(codeVerifier);
  const redirectUri = `${typeof window === 'undefined' ? getDefaultAppOrigin() : window.location.origin}/auth/handoff`;

  safeSessionStorage.setItem(authHandoffStorageKey, JSON.stringify({
    codeVerifier,
    createdAt: Date.now(),
    redirectUri,
    returnUrl,
    state,
  } satisfies AuthHandoffRecord));

  const target = new URL(buildAuthUrl(authPath));
  target.searchParams.delete('redirect');
  target.searchParams.delete('returnTo');
  target.searchParams.set('returnUrl', returnUrl);
  target.searchParams.set('handoff', '1');
  target.searchParams.set('handoff_state', state);
  target.searchParams.set('code_challenge', codeChallenge);
  target.searchParams.set('handoff_redirect', redirectUri);
  return target.toString();
};

export const buildCentralAuthBootstrapUrl = (returnUrl: string, authPath = '/auth') => {
  const target = new URL('/auth/begin', getDefaultAppOrigin());
  target.searchParams.set('returnUrl', resolveAuthReturnUrl(returnUrl));
  if (authPath !== '/auth') target.searchParams.set('authPath', authPath);
  return target.toString();
};

export const buildCentralAuthRetryUrl = () => {
  const record = readHandoffRecord();
  const fallback = typeof window === 'undefined' ? getDefaultAppOrigin() : `${window.location.origin}/`;
  return buildCentralAuthBootstrapUrl(record?.returnUrl || fallback);
};

export const buildCentralAuthCallbackUrl = (
  path: string,
  returnUrl: string,
  sourceParams: URLSearchParams,
) => {
  const target = new URL(buildAuthUrl(path));
  target.searchParams.set('returnUrl', resolveAuthReturnUrl(returnUrl));
  for (const name of ['handoff', 'handoff_state', 'code_challenge', 'handoff_redirect', 'authReturn']) {
    const value = sourceParams.get(name);
    if (value) target.searchParams.set(name, value);
  }
  return target.toString();
};

export const buildFirstPartyAuthorizeUrl = (searchParams: URLSearchParams) => {
  const state = searchParams.get('handoff_state');
  const codeChallenge = searchParams.get('code_challenge');
  const redirectValue = searchParams.get('handoff_redirect') || WERSEE_WEB_OAUTH_REDIRECT_URI;
  let redirectUri: URL;
  try { redirectUri = new URL(redirectValue); } catch { return null; }
  if (
    !WERSEE_WEB_OAUTH_CLIENT_ID
    || !isPkceValue(state)
    || !isPkceValue(codeChallenge, 43)
    || redirectUri.pathname !== '/auth/handoff'
    || !isTrustedWerseeReturnOrigin(redirectUri.origin)
    || ['id.wersee.com', 'auth.wersee.com'].includes(redirectUri.hostname.toLowerCase())
  ) {
    return null;
  }

  const authorize = new URL(`${supabaseUrl.replace(/\/+$/, '')}/auth/v1/oauth/authorize`);
  authorize.search = new URLSearchParams({
    response_type: 'code',
    client_id: WERSEE_WEB_OAUTH_CLIENT_ID,
    redirect_uri: redirectUri.toString(),
    scope: 'openid email profile',
    state: state!,
    code_challenge: codeChallenge!,
    code_challenge_method: 'S256',
  }).toString();
  return authorize.toString();
};

export const continueFromCentralAuth = (
  returnUrl: string,
  searchParams: URLSearchParams,
) => {
  const internalReturnUrl = resolveInternalAuthReturnUrl(searchParams.get('authReturn'));
  if (internalReturnUrl) {
    window.location.replace(internalReturnUrl);
    return;
  }
  if (
    import.meta.env.DEV
    || isLocalAuthHostname(window.location.hostname)
    || !isAuthHostname(window.location.hostname)
  ) {
    window.location.replace(resolveAuthReturnUrl(returnUrl));
    return;
  }
  const authorizeUrl = buildFirstPartyAuthorizeUrl(searchParams);
  if (authorizeUrl) {
    window.location.replace(authorizeUrl);
    return;
  }
  window.location.replace(buildCentralAuthBootstrapUrl(returnUrl));
};

export const finishCentralAuthHandoff = async (searchParams: URLSearchParams) => {
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const oauthError = searchParams.get('error_description') || searchParams.get('error');
  const record = readHandoffRecord();

  if (oauthError) throw new Error(oauthError);
  if (!record || !code || !state || state !== record.state) {
    safeSessionStorage.removeItem(authHandoffStorageKey);
    throw new Error('This secure sign-in handoff is invalid or has expired.');
  }
  if (record.redirectUri !== `${window.location.origin}/auth/handoff`) {
    safeSessionStorage.removeItem(authHandoffStorageKey);
    throw new Error('The secure return address does not match this Wersee service.');
  }

  const response = await fetch(`${supabaseUrl.replace(/\/+$/, '')}/auth/v1/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      client_id: WERSEE_WEB_OAUTH_CLIENT_ID,
      redirect_uri: record.redirectUri,
      code_verifier: record.codeVerifier,
    }),
  });
  const tokens = await response.json() as OAuthTokenResponse;
  if (!response.ok || !tokens.access_token || !tokens.refresh_token) {
    throw new Error(tokens.error_description || tokens.error || 'Wersee could not finish the secure sign-in handoff.');
  }

  const { error } = await supabase.auth.setSession({
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
  });
  if (error) throw error;

  safeSessionStorage.removeItem(authHandoffStorageKey);
  return resolveAuthReturnUrl(record.returnUrl);
};
